import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { NotificationPriority } from '../types';

interface NotificationBadgeProps {
    count: number;
    priority?: NotificationPriority;
    showPulse?: boolean;
    size?: 'sm' | 'md' | 'lg';
}

const NotificationBadge: React.FC<NotificationBadgeProps> = ({
    count,
    priority = 'MEDIUM',
    showPulse = true,
    size = 'md',
}) => {
    if (count === 0) return null;

    const sizeClasses = {
        sm: 'min-w-[16px] h-4 text-[10px] px-1',
        md: 'min-w-[20px] h-5 text-xs px-1.5',
        lg: 'min-w-[24px] h-6 text-sm px-2',
    };

    const priorityColors = {
        LOW: 'bg-gray-500',
        MEDIUM: 'bg-blue-600',
        HIGH: 'bg-orange-600',
        CRITICAL: 'bg-red-600',
    };

    const displayCount = count > 99 ? '99+' : count.toString();

    return (
        <div className="relative">
            <AnimatePresence>
                <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    className={`
                        ${sizeClasses[size]}
                        ${priorityColors[priority]}
                        rounded-full
                        text-white
                        font-bold
                        flex
                        items-center
                        justify-center
                        shadow-lg
                    `}
                >
                    {displayCount}
                </motion.div>
            </AnimatePresence>

            {/* Pulse animation for new notifications */}
            {showPulse && priority !== 'LOW' && (
                <span className="absolute inset-0 flex items-center justify-center">
                    <span
                        className={`
                            animate-ping
                            absolute
                            inline-flex
                            h-full
                            w-full
                            rounded-full
                            ${priorityColors[priority]}
                            opacity-75
                        `}
                    />
                </span>
            )}
        </div>
    );
};

export default NotificationBadge;
